'use client';
import { makeProfile } from '@/utils/profile';
import ProfileHeader from '@/app/_components/widgets/profile/profileHeader';
import ProfileBody from '@/app/_components/widgets/profile/profileBody';
import { fireViewItemEvent } from '@/utils/trackingGtm';
import { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { storeGoogleBusinessCookie } from '@/utils/trackingGoogleBusiness';
import Container from '@/app/_components/container';
import Specializations from '@/app/_components/widgets/profile/profileBody/specializations';
import Section from '@/app/_components/widgets/profile/section';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ProfileListing } from '@/app/_components/widgets/profile/listing';
import { ApolloProvider } from '@apollo/client';
import apolloClient from '@/utils/apolloClient';
import dynamic from 'next/dynamic';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import DeliveryModes from '@/app/_components/widgets/profile/profileBody/deliveryModes';
import Payments from '@/app/_components/widgets/profile/profileBody/payments';
import clsx from 'clsx';
import { ProfileSearch } from '@/app/_components/widgets/profile/profileSearch';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { useDisableBodyScroll } from '@/app/_hooks/useDisableBodyScroll';
import { useHeaderHeight } from '@/app/_hooks/useHeaderHeight';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { InlineBanner } from '@/app/_components/AlertBanner';
import { mapSpecializationToCard } from '@/app/_components/widgets/profile/utils';

const DynamicMap = dynamic(() => import('@/app/_components/widgets/map/interactive'), { ssr: false });
const Photos = dynamic(() => import('@/app/_components/widgets/profile/profileBody/photos'), { ssr: false });
type ProfileProps = {
  profile: ReturnType<typeof makeProfile>;
  showOverlay?: () => void;
  questions: { question: string; className?: string }[];
  faqList: ReactNode;
};

const Profile = ({ profile, questions, faqList }: ProfileProps) => {
  const isDesktopAndSmaller = useIsDesktop();
  const isWelfare = useIsWelfare();
  const [showMobileMap, setShowMobileMap] = useState(false);
  useDisableBodyScroll(showMobileMap);
  const headerHeight = useHeaderHeight();
  const [isSearchModalVisible, setSearchModalVisible] = useState(false);
  const hasSpecialities = profile.availableSpecializations.length > 0;
  // This allows the input in the modal to be focused with the keyboard open
  useEffect(() => {
    if (isDesktopAndSmaller && isSearchModalVisible) {
      const div = document.getElementById('autocomplete-centri-medici-section') as HTMLDivElement;
      const focusableElements = div.querySelectorAll<HTMLElement>('input');
      if (focusableElements.length === 0) return;
      focusableElements[0]?.focus();
      focusableElements[0]?.click();
    }
  }, [isSearchModalVisible, isDesktopAndSmaller]);

  useEffect(() => {
    if (profile.isLicensed) {
      fireViewItemEvent('0', {
        item_name: profile.name,
        item_brand: profile.description ?? '',
        item_variant: profile.name,
        item_category: profile.city,
        price: '0',
        index: 0,
      });
    }
  }, [profile]);

  const mapItem = useMemo(() => {
    if (!profile || !profile.location || profile.domiciliaryCare) return null;
    return {
      name: profile.name,
      specializations: {
        specialityChannels: profile.availableSpecializations?.[0]?.specialityChannels ?? [],
        label: profile.availableSpecializations?.[0]?.label ?? '',
      },
      address: `${profile.address?.street} ${profile.address?.streetNumber}`,
      cap: profile.address?.cap,
      city: profile.address?.cityName,
      _geoloc: { lat: profile.location.latitude, lng: profile.location.longitude },
      license: profile.license,
      ambulatoryId: profile.id,
      slug: profile.inclinicSlug,
    };
  }, [profile]);

  useEffect(() => {
    if (!profile.id) return;
    storeGoogleBusinessCookie(profile.id);
  }, [profile.id]);

  return (
    <ApolloProvider client={apolloClient}>
      {showMobileMap && mapItem && (
        <div className="absolute inset-0 top-[64px] z-[350] h-[calc(100vh-64px)] bg-white">
          <div className="flex h-full w-full flex-col">
            <div className="flex items-center justify-start px-4 py-4">
              <ELButton
                label="Torna indietro"
                leadingIcon={<ArrowLeftIcon />}
                onClick={() => setShowMobileMap(false)}
                variant="text"
                color="primary"
              />
            </div>

            <DynamicMap items={[mapItem]} setUserGeolocation={() => {}} isUserGeolocationActive={false} />
          </div>
        </div>
      )}
      <div className={clsx('relative', isWelfare && '[&_.hide-welfare]:hidden')}>
        <ProfileHeader imageSrc={profile.seo?.logo?.url} altText={profile.seo?.logo?.altText ?? profile.name} />
        <div className={clsx('bg-secondary-light-active pb-4', profile.imageSrc ? 'pt-8' : 'pt-4')}>
          <Container>
            <div className={clsx('grid grid-cols-1 gap-x-16 lg:grid-cols-[2fr_1fr]')}>
              <ProfileBody
                isLicensed={profile.isLicensed}
                name={profile.name}
                id={profile.id}
                city={profile.city}
                address={profile.completeAddress}
                availableDeliveryModes={profile.availableDeliveryModes}
                availablePayments={profile.availablePayments}
                description={profile.description}
                availableOpeningHours={profile.availableOpeningHours}
                availableSpecializations={profile.availableSpecializations}
                questions={questions}
                setShowMobileMap={setShowMobileMap}
                setSearchModalVisible={setSearchModalVisible}
                isDomicile={profile.domiciliaryCare}
              />
              {!isDesktopAndSmaller && profile.isLicensed && (
                <div className="hidden flex-col gap-y-5 lg:flex">
                  {!!profile.availableDeliveryModes?.length && (
                    <DeliveryModes deliveryModesList={profile.availableDeliveryModes} city={profile.city} />
                  )}
                  {!!profile.availablePayments?.length && <Payments paymentsList={profile.availablePayments} />}
                </div>
              )}
            </div>
          </Container>
        </div>

        {profile.isLicensed && hasSpecialities && (
          <>
            <ProfileSearch
              specializations={profile.availableSpecializations}
              searchModalVisible={isSearchModalVisible}
              setSearchModalVisible={setSearchModalVisible}
              placeholder={`Ricerca prestazione presso ${profile.name}`}
            />

            <Container>
              <div className="relative grid w-full grid-cols-1 gap-x-16 lg:grid-cols-[2fr_1fr]">
                <Suspense fallback={<div className="min-h-72" />}>
                  <ProfileListing
                    items={
                      profile.availableSpecializations.map(specialization => {
                        return mapSpecializationToCard(specialization, profile);
                      }) ?? []
                    }
                  />
                </Suspense>
                {mapItem && !isDesktopAndSmaller && (
                  <div
                    className="sticky max-h-[100vh] pt-4"
                    style={{
                      // 48 is sticky input header height - padding
                      top: headerHeight + 48,
                    }}
                  >
                    <DynamicMap items={[mapItem]} setUserGeolocation={() => {}} isUserGeolocationActive={false} />
                  </div>
                )}
              </div>
            </Container>
          </>
        )}

        <div className="bg-neutral-surface">
          <Container>
            <div className="mt-4 grid w-full grid-cols-1 gap-x-16 lg:grid-cols-[2fr_1fr]">
              <div className="flex flex-col gap-y-4">
                {hasSpecialities && <Specializations specializationsList={profile.availableSpecializations} />}
                {!hasSpecialities && profile.isLicensed && (
                  <div className="flex flex-col gap-y-4">
                    <InlineBanner
                      title="Prestazioni non disponibili"
                      description="Ci dispiace, al momento non sono disponibili prestazioni prenotabili presso questa struttura. Ti invitiamo a consultare un’altra struttura nelle vicinanze."
                    />
                    <hr className="mb-4" />
                  </div>
                )}
                {!!profile.photos?.length && <Photos items={profile.photos} />}
                <Section title="domande frequenti" testId="profile faqs" showDivider={false} id="profile-faqs-section">
                  {faqList}
                </Section>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </ApolloProvider>
  );
};

export default Profile;
